import TimeAgo from 'javascript-time-ago';
import { I18n } from '../locales/translate';
import { parseDate } from './util';
import en from 'javascript-time-ago/locale/en';
import de from 'javascript-time-ago/locale/en';
import { toFullDaysRange, validTimeframeOrUndefined, TimeFrame, Duration } from './datetime';
import { uniq } from 'lodash';
import { useEffect, useRef, useState } from 'react';

TimeAgo.addLocale(en);
TimeAgo.addLocale(de);

export function formatCurrency(value?: number | null): string | undefined {
  if (value === undefined || value === null) {
    return;
  }
  return new Intl.NumberFormat(I18n.locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

export function formatPercentage(
  value?: number | null,
  { maximumFractionDigits }: { maximumFractionDigits?: number } = { maximumFractionDigits: 4 }
): string | undefined {
  if (value === undefined || value === null || isNaN(value)) {
    return;
  }
  return new Intl.NumberFormat(I18n.locale, {
    style: 'percent',
    maximumFractionDigits,
  }).format(value);
}

export function formatNumber(value?: number, fractionDigits = 2): string | undefined {
  if (value === undefined || isNaN(value)) {
    return;
  }
  return new Intl.NumberFormat(I18n.locale, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatterLocale(): string {
  return I18n.locale === 'en' ? 'en-GB' : I18n.locale;
}

export function formatDate(
  value?: Date | string | null,
  localeOverride?: 'en' | 'de',
  options: Intl.DateTimeFormatOptions = {}
): string | undefined {
  value = parseDate(value);
  if (value === undefined || value === null) {
    return;
  }
  options = { month: '2-digit', day: '2-digit', year: 'numeric', ...options };
  return new Intl.DateTimeFormat(localeOverride || formatterLocale(), options).format(value);
}

export function formatMonth(value: Date | string, localeOverride?: 'en' | 'de'): string | undefined {
  const parsedValue = parseDate(value);

  if (parsedValue === undefined || parsedValue === null) {
    return;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    year: 'numeric',
  };

  return new Intl.DateTimeFormat(localeOverride || formatterLocale(), options).format(parsedValue);
}

export function formatRelativeDate(value: Date | string | undefined): string | undefined {
  value = parseDate(value);
  if (value === undefined) {
    return;
  }
  return new TimeAgo(formatterLocale()).format(value);
}

export function formatTime(
  value: Date | string | undefined,
  { seconds }: { seconds?: boolean } = {}
): string | undefined {
  value = parseDate(value);
  if (value === undefined) {
    return;
  }
  try {
    return new Intl.DateTimeFormat(formatterLocale(), {
      hour: 'numeric',
      minute: 'numeric',
      second: seconds ? 'numeric' : undefined,
    }).format(value);
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    return;
  }
}

export function formatDateTime(value?: Date | string | null, seconds?: boolean, weekday?: boolean): string | undefined {
  value = parseDate(value);
  if (value === undefined || value === null) {
    return;
  }
  try {
    return new Intl.DateTimeFormat(formatterLocale(), {
      weekday: weekday ? 'long' : undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      ...(seconds ? { second: 'numeric' } : {}),
    }).format(value);
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    return;
  }
}

export function ensureSuffix(value: string, suffix: string): string {
  if (value.endsWith(suffix)) {
    return value;
  }
  return `${value}${suffix}`;
}

export function deleteSuffix(value: string, suffix: string): string {
  if (value.endsWith(suffix)) {
    return value.substring(0, value.length - suffix.length);
  }
  return value;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) {
    return singular;
  }

  return plural || `${singular}s`;
}

export function generateMailToLink({
  email,
  subject,
  body,
}: {
  email: string[];
  subject: string;
  body: string;
}): string {
  return encodeURI(`mailto:${email.join(',')}?subject=${subject}&body=${body}`);
}

export function formatRangeOfDays(time: [Date | string | undefined | null, Date | string | undefined | null]): string {
  const validTime = validTimeframeOrUndefined(time);
  if (!validTime) {
    return '';
  }
  const timeToUse: TimeFrame = [validTime[0], validTime[1] || validTime[0]];
  const range = toFullDaysRange(timeToUse);
  return uniq(range.map((e) => formatDate(e))).join(' - ') || '';
}

export function normalizeNumber(n: string | number): string {
  if (typeof n === 'string') {
    return n.length === 1 ? `0${n}` : n;
  } else {
    return n < 10 ? `0${n}` : `${n}`;
  }
}

export function formatDateForDatabase(date?: Date, options?: { time: boolean }): string {
  if (!date) {
    return '';
  }

  const returnDate = `${date.getFullYear()}-${normalizeNumber(date.getMonth() + 1)}-${normalizeNumber(date.getDate())}`;

  let time = '';
  if (options && options.time) {
    time = ` ${normalizeNumber(date.getHours())}:${normalizeNumber(date.getMinutes())}:${normalizeNumber(
      date.getSeconds()
    )}`;
  }

  return returnDate + time;
}

export function useIsTextTruncated(): [React.MutableRefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (ref.current) {
      const { scrollWidth, clientWidth } = ref.current;
      setIsTruncated(scrollWidth > clientWidth);
    }
  }, []);

  return [ref, isTruncated];
}

export default useIsTextTruncated;

export function formatDateWithoutYear(value: Date | string | null, localeOverride?: 'en' | 'de'): string | undefined {
  const parsedValue = parseDate(value);
  if (value === undefined || value === null) {
    return;
  }

  return new Intl.DateTimeFormat(localeOverride || formatterLocale(), {
    month: '2-digit',
    day: '2-digit',
    year: undefined,
  }).format(parsedValue);
}

export function formatDateTimeWithoutYear(value: Date | string): string | undefined {
  const parsedValue = parseDate(value);
  if (parsedValue === undefined) {
    return;
  }
  try {
    return new Intl.DateTimeFormat(formatterLocale(), {
      year: undefined,
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
    }).format(parsedValue);
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    return;
  }
}

export function formatJsonString(jsonString: string | null | undefined): string {
  if (!jsonString || jsonString.trim() === '') {
    return '{}';
  }

  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // If parsing fails, return the original string
    return jsonString;
  }
}

type DurationAccuracy = 'days' | 'hours' | 'minutes' | 'seconds';

export function formatDuration(duration: Duration, accuracy: DurationAccuracy = 'seconds'): string {
  const units: DurationAccuracy[] = ['days', 'hours', 'minutes', 'seconds'];
  const accuracyIndex = units.indexOf(accuracy);

  const parts: string[] = [];

  if (accuracyIndex >= 0) {
    if (accuracyIndex >= 0 && duration.days > 0) parts.push(`${duration.days}d`);
    if (accuracyIndex >= 1 && duration.hours > 0) parts.push(`${duration.hours}h`);
    if (accuracyIndex >= 2 && duration.minutes > 0) parts.push(`${duration.minutes}m`);
    if (accuracyIndex >= 3 && duration.seconds > 0) parts.push(`${duration.seconds}s`);
  }

  return parts.join(' ') || '0s';
}
